import axios from 'axios';
import { 
  Group, 
  Expense, 
  User, 
  Settlement, 
  Insights, 
  CreateExpenseData, 
  CreateGroupData,
  AddMemberData,
  Balance
} from '../types';
import { logger } from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Add axios interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token and reload
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      logger.warn('Invalid token detected, clearing and reloading');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

class ApiService {
  private getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  private getHeaders() {
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  // ⚡ BATCH DASHBOARD - Get all dashboard data in ONE request
  async getDashboardData(): Promise<{
    groups: Group[];
    balances: Balance[];
    expenses: Expense[];
    settlements: Settlement[];
    features?: {
      personalExpenses: {
        enabled: boolean;
        accessType: string | null;
        expiresAt: string | null;
      };
    };
    meta: {
      responseTime: number;
      groupCount: number;
      balanceCount: number;
      expenseCount: number;
      settlementCount: number;
    };
  }> {
    const response = await axios.get(`${API_BASE_URL}/dashboard`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  // Groups
  async getGroups(): Promise<Group[]> {
    // Check for debug mode (mock data in localStorage)
    const authToken = this.getAuthToken();
    if (authToken === 'mock-token-123') {
      logger.debug('🧪 DEBUG MODE: Using mock groups from localStorage');
      const mockGroups = localStorage.getItem('mock-groups');
      if (mockGroups) {
        return JSON.parse(mockGroups);
      }
      return [];
    }

    logger.debug('🌐 API: Fetching groups from', `${API_BASE_URL}/groups`);
    logger.debug('🔑 Headers:', this.getHeaders());
    try {
      const response = await axios.get(`${API_BASE_URL}/groups`, {
        headers: this.getHeaders()
      });
      logger.debug('✅ API: Groups response:', response.data);
      return response.data.groups;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: unknown }; message?: string };
      logger.error('❌ API: Failed to fetch groups:', axiosError.response?.status, axiosError.response?.data, axiosError.message);
      throw error;
    }
  }

  async getGroup(id: string): Promise<Group> {
    const response = await axios.get(`${API_BASE_URL}/groups/${id}`, {
      headers: this.getHeaders()
    });
    return response.data.group;
  }

  async getPersonalGroup(): Promise<Group | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/groups/personal`, {
        headers: this.getHeaders()
      });
      return response.data.group;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: unknown }; message?: string };
      if (axiosError.response?.status === 404) {
        // Personal group doesn't exist yet
        return null;
      }
      throw error;
    }
  }

  async createGroup(data: CreateGroupData): Promise<Group> {
    const response = await axios.post(`${API_BASE_URL}/groups`, data, {
      headers: this.getHeaders()
    });
    return response.data.group;
  }

  async addMemberToGroup(groupId: string, data: AddMemberData): Promise<any> {
    const response = await axios.post(`${API_BASE_URL}/groups/${groupId}/members`, data, {
      headers: this.getHeaders()
    });
    return response.data.member;
  }

  async getGroupBalances(groupId: string): Promise<Balance[]> {
    // Check for debug mode
    const authToken = this.getAuthToken();
    if (authToken === 'mock-token-123') {
      logger.debug('🧪 DEBUG MODE: Using mock balances from localStorage');
      const mockBalances = localStorage.getItem('mock-balances');
      if (mockBalances) {
        const allBalances = JSON.parse(mockBalances);
        return allBalances[groupId] || [];
      }
      return [];
    }

    const response = await axios.get(`${API_BASE_URL}/groups/${groupId}/balances`, {
      headers: this.getHeaders()
    });
    return response.data.balances;
  }

  // Expenses
  async getExpenses(groupId: string): Promise<Expense[]> {
    // Check for debug mode
    const authToken = this.getAuthToken();
    if (authToken === 'mock-token-123') {
      logger.debug('🧪 DEBUG MODE: Using mock expenses from localStorage');
      const mockExpenses = localStorage.getItem('mock-expenses');
      if (mockExpenses) {
        const allExpenses = JSON.parse(mockExpenses);
        return allExpenses.filter((exp: Expense) => exp.groupId === groupId);
      }
      return [];
    }

    const response = await axios.get(`${API_BASE_URL}/expenses/group/${groupId}`, {
      headers: this.getHeaders()
    });
    return response.data.expenses;
  }

  async getExpense(id: string): Promise<Expense> {
    const response = await axios.get(`${API_BASE_URL}/expenses/${id}`, {
      headers: this.getHeaders()
    });
    return response.data.expense;
  }

  async createExpense(data: CreateExpenseData): Promise<Expense> {
    const response = await axios.post(`${API_BASE_URL}/expenses`, data, {
      headers: this.getHeaders()
    });
    return response.data.expense;
  }

  async updateExpense(id: string, data: Partial<CreateExpenseData>): Promise<Expense> {
    const response = await axios.put(`${API_BASE_URL}/expenses/${id}`, data, {
      headers: this.getHeaders()
    });
    return response.data.expense;
  }

  async deleteExpense(id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/expenses/${id}`, {
      headers: this.getHeaders()
    });
  }

  async parseExpenseText(text: string): Promise<{
    success: boolean;
    parsed?: {
      title: string;
      amount: number;
      description?: string;
      category: string;
      confidence: number;
    };
    error?: string;
  }> {
    const response = await axios.post(`${API_BASE_URL}/ai/parse-expense`, 
      { text },
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async getSpendingStatistics(userId: string, period: string = 'month'): Promise<Insights> {
    const response = await axios.get(`${API_BASE_URL}/expenses/statistics/${userId}?period=${period}`, {
      headers: this.getHeaders()
    });
    return response.data.insights;
  }

  // Users
  async searchUsers(query: string): Promise<User[]> {
    const response = await axios.get(`${API_BASE_URL}/users/search?q=${encodeURIComponent(query)}`, {
      headers: this.getHeaders()
    });
    return response.data.users;
  }

  async getUser(id: string): Promise<User> {
    const response = await axios.get(`${API_BASE_URL}/users/${id}`, {
      headers: this.getHeaders()
    });
    return response.data.user;
  }

  async updateUser(id: string, data: Partial<User>): Promise<User> {
    const response = await axios.put(`${API_BASE_URL}/users/${id}`, data, {
      headers: this.getHeaders()
    });
    return response.data.user;
  }

  // Settlements
  async getSettlements(userId: string, status: string = 'pending'): Promise<Settlement[]> {
    // Check for debug mode
    const authToken = this.getAuthToken();
    if (authToken === 'mock-token-123') {
      logger.debug('🧪 DEBUG MODE: Using mock settlements from localStorage');
      const mockSettlements = localStorage.getItem('mock-settlements');
      if (mockSettlements) {
        const allSettlements = JSON.parse(mockSettlements);
        // Filter by status if specified
        if (status === 'all') {
          return allSettlements;
        }
        return allSettlements.filter((s: any) => s.status === status);
      }
      return [];
    }

    const response = await axios.get(`${API_BASE_URL}/users/${userId}/settlements?status=${status}`, {
      headers: this.getHeaders()
    });
    return response.data.settlements;
  }

  async createSettlement(userId: string, data: { toUserId: string; amount: number; currency?: string; type?: string }): Promise<Settlement> {
    const response = await axios.post(`${API_BASE_URL}/users/${userId}/settlements`, data, {
      headers: this.getHeaders()
    });
    return response.data.settlement;
  }

  async updateSettlementStatus(settlementId: string, status: string): Promise<Settlement> {
    const response = await axios.put(`${API_BASE_URL}/users/settlements/${settlementId}`, { status }, {
      headers: this.getHeaders()
    });
    return response.data.settlement;
  }

  // AI Insights
  async getAIInsights(groupId: string): Promise<any> {
    logger.debug('🤖 API: Fetching AI insights for group', groupId);
    try {
      const response = await axios.get(`${API_BASE_URL}/ai/insights/${groupId}`, {
        headers: this.getHeaders()
      });
      logger.debug('✅ API: AI insights response:', response.data);
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: unknown }; message?: string };
      logger.error('❌ API: Failed to fetch AI insights:', axiosError.response?.status, axiosError.response?.data, axiosError.message);
      throw error;
    }
  }

  // Invoice Generation
  async generateInvoice(data: { toUserId: string; amount: number; currency: string; groupId: string; groupName: string }): Promise<any> {
    logger.debug('💸 API: Generating invoice', data);
    try {
      const response = await axios.post(`${API_BASE_URL}/users/invoice/generate`, data, {
        headers: this.getHeaders()
      });
      logger.debug('✅ API: Invoice generated:', response.data);
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: unknown }; message?: string };
      logger.error('❌ API: Failed to generate invoice:', axiosError.response?.status, axiosError.response?.data, axiosError.message);
      throw error;
    }
  }

  // AI Insights
  async getInsights(groupId: string): Promise<{ insights: any[] }> {
    logger.debug('🧠 API: Fetching AI insights for group', groupId);
    try {
      const response = await axios.get(`${API_BASE_URL}/ai/insights/${groupId}`, {
        headers: this.getHeaders()
      });
      logger.debug('✅ API: Insights response:', response.data);
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: unknown }; message?: string };
      logger.error('❌ API: Failed to fetch insights:', axiosError.response?.status, axiosError.response?.data, axiosError.message);
      // Return empty insights on error
      return { insights: [] };
    }
  }

  // TON/USDT Payment Options
  async getPaymentOptions(usdAmount: number): Promise<{ options: any[] }> {
    logger.debug('💎 API: Fetching payment options for', usdAmount, 'USD');
    try {
      const response = await axios.post(`${API_BASE_URL}/ton/payment-options`, 
        { usdAmount },
        { headers: this.getHeaders() }
      );
      logger.debug('✅ API: Payment options:', response.data);
      return response.data;
    } catch (error: unknown) {
      logger.error('❌ API: Failed to fetch payment options:', error);
      throw error;
    }
  }

  // Feature Flags
  async checkFeatureAccess(featureName: string): Promise<boolean> {
    try {
      const response = await axios.get(`${API_BASE_URL}/features/${featureName}`, {
        headers: this.getHeaders()
      });
      return response.data.hasAccess;
    } catch (error: unknown) {
      logger.error('Failed to check feature access:', error);
      return false; // Fail closed - no access if error
    }
  }

  async getUserFeatures(): Promise<string[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/features`, {
        headers: this.getHeaders()
      });
      return response.data.features;
    } catch (error: unknown) {
      logger.error('Failed to get user features:', error);
      return [];
    }
  }

  // Premium Subscriptions
  async upgradePremium(months: number = 1): Promise<any> {
    try {
      const response = await axios.post(`${API_BASE_URL}/premium/upgrade`, {
        months
      }, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: unknown) {
      logger.error('Failed to initiate premium upgrade:', error);
      throw error;
    }
  }

  async verifyPremiumPayment(payload: string, starsAmount: number): Promise<any> {
    try {
      const response = await axios.post(`${API_BASE_URL}/premium/verify-payment`, {
        payload,
        stars_amount: starsAmount
      }, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: unknown) {
      logger.error('Failed to verify premium payment:', error);
      throw error;
    }
  }

  async getPremiumStatus(): Promise<any> {
    try {
      const response = await axios.get(`${API_BASE_URL}/premium/status`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: unknown) {
      logger.error('Failed to get premium status:', error);
      throw error;
    }
  }

  // Currency Conversion
  async convertCurrency(amount: number, from: string, to: string): Promise<number> {
    try {
      const response = await axios.post(`${API_BASE_URL}/currency/convert`, {
        amount,
        from,
        to
      });
      return response.data.convertedAmount;
    } catch (error: unknown) {
      logger.error('Failed to convert currency:', error);
      // Fallback: return original amount if conversion fails
      return amount;
    }
  }

  async convertCurrencyBatch(
    amounts: Array<{ amount: number; currency: string }>, 
    targetCurrency: string
  ): Promise<{ conversions: any[]; total: number; targetCurrency: string }> {
    try {
      const response = await axios.post(`${API_BASE_URL}/currency/convert-batch`, {
        amounts,
        targetCurrency
      });
      return response.data;
    } catch (error: unknown) {
      logger.error('Failed to batch convert currencies:', error);
      // Fallback: sum without conversion
      const total = amounts.reduce((sum, item) => sum + item.amount, 0);
      return {
        conversions: amounts.map(item => ({
          originalAmount: item.amount,
          originalCurrency: item.currency,
          convertedAmount: item.amount
        })),
        total,
        targetCurrency
      };
    }
  }

  // ========================================
  // USER ENDPOINTS
  // ========================================

  async getCurrentUser(): Promise<User> {
    const response = await axios.get(`${API_BASE_URL}/users/me`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  // ========================================
  // ADMIN ENDPOINTS
  // ========================================

  async getAdminStats() {
    const response = await axios.get(`${API_BASE_URL}/admin/stats`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async sendBroadcast(payload: {
    message: string;
    parseMode?: string;
    filter?: string;
    buttons?: Array<{ text: string; web_app?: string; url?: string }>;
  }) {
    const response = await axios.post(`${API_BASE_URL}/admin/broadcast`, payload, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  // ========================================
  // REFERRAL ENDPOINTS
  // ========================================

  async getReferralStats() {
    const response = await axios.get(`${API_BASE_URL}/referrals/stats`, {
      headers: this.getHeaders()
    });
    return response.data;
  }
}

export const apiService = new ApiService();
